import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  StretchHorizontal,
  Info,
} from "lucide-react";

const PalindromicSubstringsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1",
      s: "abc",
    },
    {
      id: 2,
      name: "Example 2",
      s: "aaa",
    },
    {
      id: 3,
      name: "Complex String",
      s: "racecar",
    },
    {
      id: 4,
      name: "No Palindrome > 1",
      s: "xyz",
    },
    {
      id: 5,
      name: "Empty String",
      s: "",
    },
    {
      id: 6,
      name: "Single Character",
      s: "a",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [s, setS] = useState(defaultTestCases[0].s);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function countSubstrings(s):", indent: 0 },
    { line: "count = 0", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 0 to s.length-1:", indent: 1 },
    { line: "// Odd length palindromes", indent: 2 },
    { line: "count += expandAndCount(s, i, i)", indent: 2 },
    { line: "// Even length palindromes", indent: 2 },
    { line: "count += expandAndCount(s, i, i + 1)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return count", indent: 1 },
    { line: "", indent: 0 },
    { line: "function expandAndCount(s, left, right):", indent: 0 },
    { line: "subCount = 0", indent: 1 },
    { line: "while left >= 0 and right < s.length", indent: 1 },
    { line: "and s[left] == s[right]:", indent: 2 },
    { line: "subCount++", indent: 3 },
    { line: "left--", indent: 3 },
    { line: "right++", indent: 3 },
    { line: "return subCount", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (s) => {
    if (!s) return [];
    const allSteps = [];
    let stepCount = 0;
    let count = 0;
    let foundPalindromes = [];

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        count,
        foundPalindromes: [...foundPalindromes],
        ...details,
      });
    };

    addStep("Start: Initialize count to 0.", 1);

    for (let i = 0; i < s.length; i++) {
      // Odd length
      addStep(
        `Checking for odd-length palindromes centered at index ${i}.`,
        4,
        { center: i }
      );
      let l = i,
        r = i;
      while (l >= 0 && r < s.length && s[l] === s[r]) {
        const currentPalindrome = s.substring(l, r + 1);
        count++;
        foundPalindromes.push(currentPalindrome);
        addStep(
          `Found palindrome: "${currentPalindrome}". Count is now ${count}.`,
          15,
          { center: i, left: l, right: r, currentPalindrome }
        );
        l--;
        r++;
      }

      // Even length
      addStep(
        `Checking for even-length palindromes centered at indices ${i}, ${
          i + 1
        }.`,
        6,
        { center: i }
      );
      l = i;
      r = i + 1;
      while (l >= 0 && r < s.length && s[l] === s[r]) {
        const currentPalindrome = s.substring(l, r + 1);
        count++;
        foundPalindromes.push(currentPalindrome);
        addStep(
          `Found palindrome: "${currentPalindrome}". Count is now ${count}.`,
          15,
          { center: i, left: l, right: r, currentPalindrome }
        );
        l--;
        r++;
      }
    }

    addStep(`Finished. Total palindromic substrings: ${count}.`, 9, {
      isFinal: true,
      left: -1,
      right: -1,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(s);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [s]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setS(testCases[index].s);
  };

  const addCustomTestCase = () => {
    try {
      if (!customInput) throw new Error("String cannot be empty.");
      const newTestCase = { id: Date.now(), name: `Custom`, s: customInput };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid input: ${error.message}`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {
    description: "Initializing...",
    count: 0,
    foundPalindromes: [],
    left: -1,
    right: -1,
    center: -1,
    isFinal: false,
    currentPalindrome: "",
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <StretchHorizontal className="text-blue-500" /> Palindromic Substrings
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a string `s`, return the number of palindromic substrings
                in it. A substring is a contiguous sequence of characters within
                the string.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">
                      s = "{tc.s}"
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom string: abba"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative h-20">
              <div className="flex flex-wrap gap-1">
                {(s || "").split("").map((char, idx) => {
                  const isCurrent =
                    idx >= currentStepData.left && idx <= currentStepData.right;
                  return (
                    <div key={idx} className="relative text-center">
                      <div
                        className={`w-10 h-10 flex items-center justify-center border-2 rounded font-mono text-xl font-bold transition-all duration-300
                                  ${
                                    isCurrent
                                      ? "bg-blue-100 border-blue-300"
                                      : "bg-gray-100 border-gray-300"
                                  }`}
                      >
                        {char}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">{idx}</span>
                      {currentStepData.left === idx && (
                        <span className="absolute -bottom-5 text-xs font-bold text-blue-600">
                          L
                        </span>
                      )}
                      {currentStepData.right === idx && (
                        <span className="absolute -bottom-5 text-xs font-bold text-blue-600">
                          R
                        </span>
                      )}
                      {currentStepData.center === idx && (
                        <span className="absolute -top-6 text-xs font-bold text-red-600">
                          Center
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-4 text-center">
              <div className="col-span-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Found Palindromes
                </h4>
                <div className="p-2 bg-blue-50 text-blue-800 font-mono rounded mt-1 flex flex-wrap gap-2 justify-center min-h-[30px]">
                  {currentStepData?.foundPalindromes.map((p, i) => (
                    <span key={i} className="px-2 bg-blue-200 rounded">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700">
                  Total Count
                </h4>
                <p className="p-2 bg-green-50 text-green-800 font-bold text-2xl rounded mt-1">
                  {currentStepData?.count || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Expand from Center Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              This approach iterates through each character of the string,
              treating it as a potential center of a palindrome. From each
              center, it expands outwards. Every time the expansion is
              successful (the `left` and `right` characters match), we count it
              as a new palindromic substring. This is done for both odd-length
              (centered at `i, i`) and even-length (centered at `i, i+1`)
              palindromes.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N^2)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N is the length of the string.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Every single character is a palindrome, so the count will
                  always be at least the length of the string.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Unlike finding the *longest* palindrome, here we increment our
                  count for *every* valid expansion step.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  This problem shares the exact same "Expand from Center"
                  algorithmic pattern as finding the longest palindromic
                  substring.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded border-2 border-red-500"></div>
                <span>Center of Expansion</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-blue-100 border border-blue-300">
                  abc
                </div>
                <span>Current Palindrome Being Checked</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-blue-600">L / R</span>
                <span>Expansion Pointers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PalindromicSubstringsVisualizer;
